import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Clock, X } from 'lucide-react';
import userWebSocketService from '../../services/userWebSocketService';
import noReplyResolveService from '../../services/noReplyResolveService';
import { showError, showSuccess } from '../../utils/toast';

const STORAGE_KEY = 'inactive_chat_reminder';

const readStoredPrompt = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

const persistPrompt = (prompt) => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        prompt_id: prompt.promptId,
        conversation_id: prompt.conversationId,
        customer_name: prompt.customerName,
        message: prompt.message,
        resolve_message: prompt.resolveMessage,
        expires_at: prompt.expiresAt,
      })
    );
  } catch (error) {
    console.warn('Failed to persist inactive chat reminder prompt to localStorage:', error);
    // Ignore storage failures (e.g. private mode) — the modal still works in-memory.
  }
};

const clearStoredPrompt = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    // no-op
  }
};

const normalizePayload = (payload) => {
  if (!payload || payload.prompt_id == null || payload.conversation_id == null) {
    return null;
  }
  return {
    promptId: payload.prompt_id,
    conversationId: payload.conversation_id,
    customerName: payload.customer_name || 'Customer',
    message:
      payload.message ||
      'The customer is not replying to your message. Do you want to resolve this conversation?',
    resolveMessage: payload.resolve_message || '',
    expiresAt: payload.expires_at || null,
  };
};

const getRemainingSeconds = (expiresAt) => {
  if (!expiresAt) return null;
  const diffMs = new Date(expiresAt).getTime() - Date.now();
  if (Number.isNaN(diffMs)) return null;
  return Math.max(0, Math.round(diffMs / 1000));
};

const FIXED_COUNTDOWN_SECONDS = 10;

const formatCountdown = (totalSeconds) => {
  if (totalSeconds == null) return '--:--';
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const InactiveChatReminder = () => {
  const [prompt, setPrompt] = useState(null);
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const [submittingAction, setSubmittingAction] = useState(null);
  const promptRef = useRef(null);

  const closeReminder = useCallback(() => {
    promptRef.current = null;
    setPrompt(null);
    setRemainingSeconds(null);
    setSubmittingAction(null);
    clearStoredPrompt();
  }, []);

  const openReminder = useCallback((nextPrompt) => {
    promptRef.current = nextPrompt;
    persistPrompt(nextPrompt);
    setPrompt(nextPrompt);
    setRemainingSeconds(nextPrompt.expiresAt != null ? FIXED_COUNTDOWN_SECONDS : null);
    setSubmittingAction(null);
  }, []);

  // Restore an in-flight prompt after a page reload (if it has not expired yet).
  useEffect(() => {
    const stored = readStoredPrompt();
    const restored = normalizePayload(stored);
    if (!restored) {
      if (stored) clearStoredPrompt();
      return;
    }
    const remaining = getRemainingSeconds(restored.expiresAt);
    if (remaining === 0) {
      clearStoredPrompt();
      return;
    }
    openReminder(restored);
  }, [openReminder]);

  // Subscribe to the User WebSocket events (does not disturb existing listeners).
  useEffect(() => {
    const handlePrompt = (payload) => {
      const nextPrompt = normalizePayload(payload);
      if (!nextPrompt) return;
      openReminder(nextPrompt);
    };

    const handleDismissed = (payload) => {
      // Only dismiss if it targets the currently shown prompt (or no id is given).
      const current = promptRef.current;
      if (!current) return;
      const dismissedConversationId = payload?.conversation_id;
      const dismissedPromptId = payload?.prompt_id;
      const matches =
        dismissedConversationId == null && dismissedPromptId == null
          ? true
          : (dismissedConversationId != null &&
              String(dismissedConversationId) === String(current.conversationId)) ||
            (dismissedPromptId != null &&
              String(dismissedPromptId) === String(current.promptId));
      if (matches) {
        closeReminder();
      }
    };

    userWebSocketService.on('conversation.no_reply_resolve_prompt', handlePrompt);
    userWebSocketService.on('conversation.no_reply_resolve_prompt_dismissed', handleDismissed);

    return () => {
      userWebSocketService.off('conversation.no_reply_resolve_prompt', handlePrompt);
      userWebSocketService.off('conversation.no_reply_resolve_prompt_dismissed', handleDismissed);
    };
  }, [openReminder, closeReminder]);

  // Countdown timer — fixed 10s local countdown, independent of the backend's expires_at.
  useEffect(() => {
    if (!prompt || !prompt.expiresAt) return undefined;

    const intervalId = setInterval(() => {
      setRemainingSeconds((prev) => (prev == null || prev <= 1 ? 0 : prev - 1));
    }, 1000);

    return () => clearInterval(intervalId);
  }, [prompt]);

  // Auto-close the reminder once the fixed countdown reaches zero.
  useEffect(() => {
    if (prompt?.expiresAt && remainingSeconds === 0) {
      // Auto-resolve timer expired — backend resolves it, so just hide locally.
      closeReminder();
    }
  }, [remainingSeconds, prompt, closeReminder]);

  const handleRespond = useCallback(
    async (action) => {
      if (submittingAction) return;

      // Use the stored socket values (conversation_id in URL, prompt_id in payload).
      const stored = readStoredPrompt();
      const conversationId = stored?.conversation_id ?? promptRef.current?.conversationId;
      const promptId = stored?.prompt_id ?? promptRef.current?.promptId;

      if (conversationId == null || promptId == null) {
        closeReminder();
        return;
      }

      setSubmittingAction(action);
      try {
        const result = await noReplyResolveService.respondToNoReplyPrompt(conversationId, {
          promptId,
          resolve: action === 'yes',
        });

        if (result.isSuccess) {
          showSuccess(
            action === 'yes'
              ? 'Conversation resolved.'
              : 'Continuing to wait for the customer.'
          );
          closeReminder();
        } else {
          showError(result.error?.message || 'Failed to submit your response. Please try again.');
          setSubmittingAction(null);
        }
      } catch (error) {
        showError('Failed to submit your response. Please try again.');
        setSubmittingAction(null);
      }
    },
    [submittingAction, closeReminder]
  );

  if (!prompt) return null;

  const isSubmitting = submittingAction !== null;
  const hasTimer = prompt.expiresAt != null && remainingSeconds != null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-[100000]" />

      {/* Centered reminder card */}
      <div className="fixed inset-0 z-[100001] flex items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">

          <div className="px-8 pt-8 pb-7 text-center">
            {/* Warning icon */}
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-amber-50">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-amber-400">
                <AlertTriangle className="h-6 w-6 text-white" fill="currentColor" stroke="white" />
              </div>
            </div>

            <h2 className="text-xl font-bold text-gray-900">Inactive Chat Reminder</h2>

            {/* Customer name (blue) + message from socket */}
            <p className="mt-3 text-[15px] leading-relaxed text-gray-600">
              <span className="font-semibold text-blue-600">{prompt.customerName}</span>{' '}
              {prompt.message}
            </p>

            {/* Auto-resolve countdown */}
            {hasTimer && (
              <div className="mt-5 flex items-start gap-3 rounded-xl border border-gray-200 bg-gray-50/60 px-4 py-4 text-left">
                <Clock className="mt-0.5 h-6 w-6 flex-shrink-0 text-blue-600" />
                <div>
                  <p className="text-[15px] text-gray-700">
                    Auto-resolve in{' '}
                    <span className="font-bold text-blue-600">
                      {formatCountdown(remainingSeconds)}
                    </span>
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    This chat will be automatically resolved if no response is received.
                  </p>
                </div>
              </div>
            )}

            <div className="my-5 border-t border-gray-200" />

            <p className="text-[15px] font-semibold text-gray-900">
              Do you want to resolve this chat now?
            </p>

            {/* Actions */}
            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={() => handleRespond('no')}
                disabled={isSubmitting}
                className="flex-1 rounded-lg border border-blue-200 bg-white px-4 py-3 text-sm font-medium text-blue-600 transition-colors hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submittingAction === 'no' ? 'Please wait...' : 'No, Continue Waiting'}
              </button>
              <button
                type="button"
                onClick={() => handleRespond('yes')}
                disabled={isSubmitting}
                className="flex-1 rounded-lg bg-blue-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submittingAction === 'yes' ? 'Resolving...' : 'Yes, Resolve Now'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InactiveChatReminder;
